/**
 * The bitmask helpers the plan's cuisine lookup needs. The mobile app's
 * module has a fuller set (add / remove / toggle / count …); only reading
 * names out of a mask is used here.
 */

/** True when `flag` is set in `currentMask`. */
export const hasFlag = (currentMask: number, flag: number): boolean =>
  (currentMask & flag) === flag;

/**
 * The names of the flags set in `currentMask`, in `flagMap` order.
 * @example
 * getFlagNames(7, { 1: "Non-specific", 2: "Indian", 4: "African" })
 * // ["Non-specific", "Indian", "African"]
 */
export const getFlagNames = (
  currentMask: number,
  flagMap: Record<number, string>
): string[] =>
  Object.keys(flagMap)
    .map(Number)
    .filter((flag) => hasFlag(currentMask, flag))
    .map((flag) => flagMap[flag]);

export interface BitmaskField {
  id: number;
  name: string;
  machineName: string;
  selected?: boolean;
}

export interface BitmaskTypeCollection<T extends number = number> {
  [key: number]: BitmaskField;
  allIds: T[];
  allFlags: T[];
}

/** A lookup collection (by flag value, plus `allFlags`) from flag definitions. */
export const createBitmaskCollection = <T extends number>(
  flags: Array<{ value: T; name: string; machineName: string }>
): BitmaskTypeCollection<T> => {
  const collection: any = {};
  const allFlags: T[] = [];

  flags.forEach(({ value, name, machineName }) => {
    collection[value] = { id: value, name, machineName, selected: false };
    allFlags.push(value);
  });

  collection.allFlags = allFlags;
  collection.allIds = allFlags;

  return collection;
};
